/**
 * The three recorded acts on an order that are NOT transitions. ROAD-5.
 * OWNER: web-frontend-engineer.
 *
 * `NON_TRANSITION_ACTIONS` (OPS_SPEC §2.4) has had these on the server all
 * along — a note, who is making it, and moving a promised date — and the order
 * screen showed their RESULTS with no way to perform them. The header said
 * "Cutter — nobody yet" and there was nothing to press; the appendix listed
 * notes nobody could add.
 *
 * ── WHY THEY SIT TOGETHER AND BELOW THE TRANSITIONS ───────────────────────
 *
 * Because none of them changes the state of the order, and the whole reason
 * OPS_SPEC keeps two lists is that a person must be able to tell the difference.
 * "Record the cloth cut" moves a suit down the line; "who is making it" does
 * not, however important it is. Putting them in the same control would make the
 * transition list a menu of everything you can do, which is exactly how somebody
 * cuts cloth while meaning to leave a note.
 *
 * ── THE THREE ARGUMENTS ───────────────────────────────────────────────────
 *
 * **A note has an audience, and it is a choice made every time.** `internal`
 * versus `client_visible` is not a checkbox tucked under the box — it is two
 * named options, because a note the client sees appears on his order page in the
 * tailor's own words, unchanged, and a person writing "the cloth was flawed,
 * mill are being difficult" needs to know which one he picked before he presses.
 *
 * **An empty cutter is not neutral.** The screen says so: leaving it empty means
 * nobody has been asked yet. A blank in a back office reads as "not applicable"
 * unless something says otherwise.
 *
 * **A promised date is moved with a reason, in front of you.** The server
 * requires one and this form says what it is for: it is what you would say to
 * the man. And the confirmation says the thing the app cannot do — *he has not
 * been told; that is still yours* — because the worst outcome here is a tailor
 * who believes the system wrote to a client and did not.
 */

import { useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import { api } from '../lib/api.ts';
import { useSubmission } from '../lib/resource.ts';
import type { AtelierOrderPayload } from '../lib/contracts.ts';
import { Button, FactLine, RovingRadioGroup, Statement, TextInput } from './primitives.tsx';
import { PromisedWindowField, RefusalStatement } from './backoffice.tsx';
import { OfflineStatement } from './states.tsx';

const VISIBILITY = ['internal', 'client_visible'] as const;

export function OrderNoteForm({ data, onWrote }: { data: AtelierOrderPayload; onWrote: () => void }) {
  const submission = useSubmission();
  const [note, setNote] = useState('');
  const [visibility, setVisibility] = useState<string>('internal');
  const [done, setDone] = useState(false);

  const add = () => {
    if (note.trim() === '') return;
    void submission
      .run(() =>
        api.addNote(data.orderId, {
          note: note.trim(),
          visibility: visibility as 'internal' | 'client_visible',
        }),
      )
      .then((result) => {
        if (result === null) return;
        setNote('');
        setDone(true);
        onWrote();
      });
  };

  return (
    <section className="pt-block">
      <h3 className="pt-record__groupHeading">{t('ordnote.heading')}</h3>
      <Statement>{t('ordnote.lede')}</Statement>
      <TextInput
        id="pt-ordnote"
        label={t('ordnote.label')}
        value={note}
        onChange={(next) => {
          setNote(next);
          setDone(false);
        }}
        multiline
        autoComplete="off"
      />
      {/* NOT a checkbox under the box. Two named audiences, chosen every time. */}
      <fieldset className="pt-filter">
        <legend className="pt-filter__legend">{t('ordnote.visibility.label')}</legend>
        <RovingRadioGroup
          ariaLabel={t('ordnote.visibility.label')}
          values={VISIBILITY}
          chosen={visibility}
          labelOf={(value) => tDynamic(`ordnote.visibility.${value}`)}
          onChoose={setVisibility}
        />
      </fieldset>
      {visibility === 'client_visible' ? (
        <FactLine>{t('ordnote.visibility.help')}</FactLine>
      ) : null}
      {submission.rejection === null ? null : <RefusalStatement rejection={submission.rejection} />}
      {submission.offline ? <OfflineStatement /> : null}
      {done ? <FactLine>{t('ordnote.done')}</FactLine> : null}
      <div className="pt-actions">
        <Button variant="secondary" onClick={add} busy={submission.busy}>
          {t('ordnote.submit')}
        </Button>
      </div>
    </section>
  );
}

export function AssignCutterForm({
  data,
  onWrote,
}: {
  data: AtelierOrderPayload;
  onWrote: () => void;
}) {
  const submission = useSubmission();
  const [name, setName] = useState('');
  const [done, setDone] = useState(false);

  const assign = () => {
    void submission
      .run(() =>
        api.assign(data.orderId, {
          assignedCutter: name.trim() === '' ? null : name.trim(),
        }),
      )
      .then((result) => {
        if (result === null) return;
        setDone(true);
        onWrote();
      });
  };

  return (
    <section className="pt-block">
      <h3 className="pt-record__groupHeading">{t('assign.heading')}</h3>
      <Statement>{t('assign.lede')}</Statement>
      <FactLine label={t('assign.label')}>
        {data.assignedCutterName ?? t('assign.none')}
      </FactLine>
      {/* A CHOICE BETWEEN REAL PEOPLE, not a free-text box. The column is a
          foreign key to `users`, and a typed name used to reach the database
          and come back as an unhandled 500. The server now refuses a value
          that is not a person — and this control means it never has to. */}
      <div className="pt-input">
        <label className="pt-input__label" htmlFor="pt-assign">
          {t('assign.label')}
        </label>
        <select
          id="pt-assign"
          className="pt-input__field"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setDone(false);
          }}
        >
          {/* "Nobody yet" is a real answer, and taking a name back off is a
              real act: a cutter leaves, and an order that keeps naming them
              sends every question about that suit to somebody who is gone. */}
          <option value="">{t('assign.none')}</option>
          {(data.assignableStaff ?? []).map((person) => (
            <option key={person.userId} value={person.userId}>
              {person.displayName}
            </option>
          ))}
        </select>
        <p className="pt-input__hint">{t('assign.help')}</p>
      </div>
      {submission.rejection === null ? null : <RefusalStatement rejection={submission.rejection} />}
      {submission.offline ? <OfflineStatement /> : null}
      {done ? <FactLine>{t('assign.done')}</FactLine> : null}
      <div className="pt-actions">
        <Button variant="secondary" onClick={assign} busy={submission.busy}>
          {t('assign.submit')}
        </Button>
      </div>
    </section>
  );
}

export function RepromiseForm({
  data,
  onWrote,
}: {
  data: AtelierOrderPayload;
  onWrote: () => void;
}) {
  const submission = useSubmission();
  const [window_, setWindow] = useState({ earliest: '', latest: '' });
  const [reason, setReason] = useState('');
  const [done, setDone] = useState(false);

  const move = () => {
    if (window_.earliest === '' || window_.latest === '' || reason.trim() === '') return;
    void submission
      .run(() =>
        api.promise(data.orderId, {
          promisedWindow: { ...window_ },
          reason: reason.trim(),
        }),
      )
      .then((result) => {
        if (result === null) return;
        setWindow({ earliest: '', latest: '' });
        setReason('');
        setDone(true);
        onWrote();
      });
  };

  return (
    <section className="pt-block">
      <h3 className="pt-record__groupHeading">{t('repromise.heading')}</h3>
      <Statement>{t('repromise.lede')}</Statement>
      {data.promisedWindow === null ? <FactLine>{t('repromise.first')}</FactLine> : null}
      {/* EMPTY ON LOAD with a labelled one-action fill — the same control the
          confirmation gate uses, and for the same reason: a date nobody typed
          is a date nobody checked. */}
      <PromisedWindowField
        earliest={window_.earliest}
        latest={window_.latest}
        onChange={setWindow}
        houseWindow={null}
      />
      <TextInput
        id="pt-repromise-reason"
        label={t('repromise.reason.label')}
        value={reason}
        onChange={(next) => {
          setReason(next);
          setDone(false);
        }}
        multiline
        autoComplete="off"
      />
      {submission.rejection === null ? null : <RefusalStatement rejection={submission.rejection} />}
      {submission.offline ? <OfflineStatement /> : null}
      {/* The thing the app cannot do, said plainly. */}
      {done ? <FactLine>{t('repromise.done')}</FactLine> : null}
      <div className="pt-actions">
        <Button variant="secondary" onClick={move} busy={submission.busy}>
          {t('repromise.submit')}
        </Button>
      </div>
    </section>
  );
}
